import type { DebounceSettings, DebouncedFunc } from "lodash-es";
import { debounce } from "lodash-es";
import { useMemo } from "react";

import { useLatest, useUnmount } from "../";

import { IgnoreAny, isDev, isFunction } from "./helpers";

export type DebouncedFunction = (...args: IgnoreAny[]) => IgnoreAny;

/**
 * @title useDebounceFn
 * @returns A object with the following elements:
 * - run: execute function.
 * - cancel: cancel execute function.
 * - flush:  immediately execute function
 */
export type UseDebounceFn = <T extends DebouncedFunction>(
  fn: T,

  wait?: number,

  options?: DebounceSettings
) => {
  run: (...args: [...Parameters<T>]) => ReturnType<T> | undefined;
  cancel: () => void;
  flush: () => void;
};

export const useDebounceFn: UseDebounceFn = <T extends DebouncedFunction>(
  fn: T,
  wait?: number,
  options?: DebounceSettings
) => {
  if (isDev) {
    if (!isFunction(fn)) {
      console.error(`useDebounceFn expected parameter is a function, got ${typeof fn}`);
    }
  }

  const fnRef = useLatest(fn);

  const debounced = useMemo(
    () =>
      debounce(
        (...args: [...Parameters<T>]): ReturnType<T> => {
          return fnRef.current(...args);
        },
        wait,
        options
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(options), wait]
  );

  useUnmount(() => {
    debounced.cancel();
  });

  return useMemo(
    () => ({
      run: (...args: [...Parameters<T>]) => debounced(...args),
      cancel: debounced.cancel,
      flush: debounced.flush,
    }),
    [debounced]
  );
};
